// Import core lib dependencies.
import moment 			from 'moment';

// Config.
import Config			from '../config/config.js';

/**
 * <pre>
 * Milestone utility methods.
 * </pre>
 *
 * @author Tim Loram
 *
 * @memberof module:Goal.Util
 * @namespace
 */
const Milestone = {

	/**
	 * Utility method to get the active milestone.
	 *
	 */
	getActiveMilestone: function(milestones) {

		let activeMilestone;

		for(let milestoneKey in milestones) {

			let milestone = milestones[milestoneKey];

			// If we are between the milestone starting, and the milestone ending.
			if(Milestone.hasMilestoneStarted(milestone) && !Milestone.hasMilestoneEnded(milestone)){
				activeMilestone = milestone;
				break;
			}
		}

		return activeMilestone;
	},

	/**
	 * Utility method to get the most recently or currently active milestone.
	 *
	 */
	getMostRecentlyActiveMilestone: function(milestones) {

		let recentMilestone;

		for(let milestoneKey in milestones) {

			let milestone = milestones[milestoneKey];

			// If milestone has already started.
			if(Milestone.hasMilestoneStarted(milestone)){
				recentMilestone = milestone;
			}
		}

		return recentMilestone;
	},

	/**
	 * Utility method to get the most recently or currently active milestone index.
	 *
	 */
	getMostRecentlyActiveMilestoneIndex: function(milestones) {

		let recentMilestoneIndex;

		for(let milestoneKey in milestones) {

			let milestone = milestones[milestoneKey];

			// If milestone has already started.
			if(Milestone.hasMilestoneStarted(milestone)){
				recentMilestoneIndex = parseInt(milestoneKey);
			}
		}

		return recentMilestoneIndex;
	},

	/**
	 * Utility method to get the next milestone after the active one.
	 *
	 */
	getNextMilestone: function(milestones) {

		let nextMilestone;

		let recentMilestoneIndex = Milestone.getMostRecentlyActiveMilestoneIndex(milestones);

		if(milestones.length > (recentMilestoneIndex+1)) {
			nextMilestone = milestones[recentMilestoneIndex+1];
		}

		return nextMilestone;
	},

	/**
	 * Utility method to calculate if any milestone is in an active period.
	 *
	 */
	isAnyMilestoneActive: function(milestones) {

		let active = false;

		for(let milestoneKey in milestones) {

			let milestone = milestones[milestoneKey];

			// If we are between the milestone starting, and the milestone ending.
			if(Milestone.hasMilestoneStarted(milestone) && !Milestone.hasMilestoneEnded(milestone)){
				active = true;
				break;
			}
		}

		return active;
	},

	/**
	 * Utility method to calculate if the given milestone is in an active period.
	 *
	 */
	isMilestoneActive: function(milestone) {

		let active = false;

		// If we are between the milestone starting, and the milestone ending.
		if(Milestone.hasMilestoneStarted(milestone) && !Milestone.hasMilestoneEnded(milestone)){
			active = true;
		}

		return active;
	},

	/**
	 * Utility method to calculate if the given milestone period has already started.
	 *
	 */
	hasMilestoneStarted: function(milestone) {

		let started = false;

		// The current datetime.
		let now = moment();

		// Milestone start datetime (from unix timestamp).
		let milestoneStart = moment(milestone.startDate);

		if(now.isAfter(milestoneStart)) {
			started = true;
		}

		return started;
	},

	/**
	 * Utility method to calculate if the given milestone period has already ended.
	 *
	 */
	hasMilestoneEnded: function(milestone) {

		let ended = false;

		// The current datetime.
		let now = moment();

		// Milestone end datetime (from unix timestamp).
		let milestoneEnd = moment(milestone.endDate);

		if(now.isAfter(milestoneEnd)) {
			ended = true;
		}

		return ended;
	},

	/**
	 * Utility method to calculate if the given milestone has been reached.
	 *
	 */
	hasMilestoneBeenReached: function(milestone, progress) {

		let reached = false;

		if(progress.milestones[milestone.id]) {
			if(progress.milestones[milestone.id].percent === 100) {
				reached = true;
			}
		}

		return reached;
	},

	/**
	 * Utility method to store the fact that a user has already participated for the given goal and milestone.
	 *
	 */
	storeUserMilestoneParticipation: function(metricId, milestone) {

		// Get Storage service.
		let storageService = NGXD.getServiceContainer().getService('storage');

		// Attempt to get storage object.
		let goalStorage = storageService.get(Config.storageKey);

		// Parse the JSON string into an object.
		if(goalStorage) {

			goalStorage = JSON.parse(goalStorage);

		// Otherwise, initialise storage.
		} else {

			goalStorage = {};
		}

		// Record the fact that this user has already participated for this Metric+Milestone.
		goalStorage[metricId] = {
			milestoneId:milestone.id
		}

		storageService.set(Config.storageKey, JSON.stringify(goalStorage));
	},

	/**
	 * Utility method to calculate if the user has already participated for the given milestone.
	 *
	 */
	hasUserAlreadyParticipatedForMilestone: function(metricId, milestone) {

		let alreadyParticipated = false;

		// Get Storage service.
		let storageService = NGXD.getServiceContainer().getService('storage');

		// Check for existing localstorage/cookie.
		let goalStorage = storageService.get(Config.storageKey);
		if(goalStorage) {

			// Parse the JSON string into an object.
			goalStorage = JSON.parse(goalStorage);

			// Check if an entry for this Metric Id is already set.
			if(goalStorage[metricId]) {

				// Check if the given Milestone within this Metric Id has been set.
				if(goalStorage[metricId].milestoneId == milestone.id) {
					alreadyParticipated = true;
				}
			}
		}

		return alreadyParticipated;
	}
}

export default Milestone;